import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DataTable from './common/DataTable';
import Alert from './common/Alert';
import AsistentesClasicosService from '../services/AsistentesClasicosService';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import RegionalService from '../services/RegionalService';
import { filterAdminMenuItems, isAdminArea, isAdminRegional } from '../utils/adminMenu';
import ProcesosMenu from './common/ProcesosMenu';
import './MyAssistants.css';

const FEATURE_FLAG_NAME = 'AsistentesClasicos GPT';
const ADMIN_MENU_ITEMS = [
  { id: 'resumen', label: 'Dashboard', icon: 'fas fa-chart-line' },
  { id: 'profesores', label: 'Profesores', icon: 'fas fa-chalkboard-teacher' },
  // { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'areas', label: 'Areas', icon: 'fas fa-sitemap' },
  { id: 'admin-area', label: 'Admins de área', icon: 'fas fa-user-tie' },
  { id: 'materias', label: 'Materias', icon: 'fas fa-book' },
  { id: 'asistentes', label: 'Asistentes OpenAI', icon: 'fas fa-robot' },
  { id: 'asistentes-gpt', label: 'Asistentes GPT', icon: 'fas fa-robot' }
];

const MyGptAssistants = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const roleName = (user?.roleName || user?.nombreRol || user?.role || user?.rol || '').toLowerCase();
  const isAdminUser = Boolean(user) && (isAdminArea(user) || isAdminRegional(user) || roleName.includes('admin'));
  const featureFlags = RegionalService.getFeatureFlags();
  const isGptClassicEnabled = featureFlags.some((flag) => {
    const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
    return nombre === FEATURE_FLAG_NAME.toLowerCase() && Boolean(flag?.activo ?? flag?.Activo);
  });
  const isGptClassicAllowed = Boolean(user) && (isGptClassicEnabled || isAdminArea(user));
  const filteredAdminMenuItems = filterAdminMenuItems(ADMIN_MENU_ITEMS, user)
    .filter((item) => isGptClassicEnabled || isAdminArea(user) || item.id !== 'asistentes-gpt');
  
  // Estados
  const [asistentes, setAsistentes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, url: '', name: '' });
  const isLoadingRef = useRef(false);

  // Cargar asistentes al montar el componente
  useEffect(() => {
    if (authLoading) return;

    if (!isGptClassicAllowed) {
      setIsLoading(false);
      return;
    }

    if (!hasLoaded && !isLoadingRef.current) {
      loadAsistentes();
    }
  }, [hasLoaded, isGptClassicAllowed, authLoading]);

  const loadAsistentes = async () => {
    if (authLoading) return;
    if (!isGptClassicAllowed) {
      setIsLoading(false);
      return;
    }
    // Evitar llamadas duplicadas
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setAlert({ show: false, message: '', type: 'error', title: '' });
      
      const response = await AsistentesClasicosService.getMisAsistentes();
      
      if (response.ok && response.asistentes) {
        const filtered = response.asistentes.filter((item) => {
          const flag = item?.gptClasicoCreacionFinalizada ?? item?.GPTClasicoCreacionFinalizada;
          const url = item?.gptClasicoURLPublica ?? item?.GPTClasicoURLPublica;
          return flag === true && !!url;
        });
        if (filtered.length === 0 && response.asistentes.length > 0) {
          setAlert({
            show: true,
            message: 'Tus GPT Clásicos están en proceso de creación. Actualiza en unos minutos.',
            type: 'info',
            title: 'Solicitudes en curso'
          });
        }
        setAsistentes(filtered);
      } else {
        throw new Error(response.error || 'Error al cargar los GPTs');
      }
      
    } catch (error) {
      console.error('Error al cargar asistentes:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los GPTs. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentes([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
      isLoadingRef.current = false;
    }
  };

  const handleRefresh = () => {
    if (!isGptClassicAllowed) return;
    setHasLoaded(false); // Permitir nueva carga
    isLoadingRef.current = false; // Resetear el flag de carga
    loadAsistentes();
  };

  const handleNewAssistant = () => {
    navigate('/new-gpt-assistant');
  };

  const handleLogout = () => {
    logout();
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const closeShareModal = () => {
    setShareModal({ open: false, url: '', name: '' });
  };

  const handleShareAssistant = async (assistant) => {
    const url = assistant?.gptClasicoURLPublica ?? assistant?.GPTClasicoURLPublica;
    if (!url) {
      setAlert({
        show: true,
        message: 'Este GPT no tiene una URL pública disponible aún.',
        type: 'info',
        title: 'Sin URL pública'
      });
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }

      setShareModal({
        open: true,
        url,
        name:
          assistant?.gptClasicoNombre ||
          assistant?.GPTClasicoNombre ||
          assistant?.nombreAsistente ||
          assistant?.NombreAsistente ||
          assistant?.nombre ||
          assistant?.Nombre ||
          ''
      });
    } catch (error) {
      console.error('Error al copiar URL:', error);
      setAlert({
        show: true,
        message: 'No se pudo copiar la URL. Inténtalo nuevamente.',
        type: 'error',
        title: 'Error al copiar'
      });
    }
  };
  const handleTestAssistant = (assistant) => {
    const url = assistant?.gptClasicoURLPublica ?? assistant?.GPTClasicoURLPublica;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEditAssistant = (assistant) => {
    if (!assistant?.idOpenAIAssistant) return;
    navigate(`/gpt-assistants/${assistant.idOpenAIAssistant}/edit`);
  };


  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isGptClassicAllowed) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Esta sección está disponible únicamente cuando la feature flag AsistentesClasicos GPT está activada.</p>
        </div>
      </div>
    );
  }

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const parsed = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del GPT',
      sortable: true,
      className: 'font-weight-bold',
      render: (value, item) =>
        item.gptClasicoNombre ||
        item.GPTClasicoNombre ||
        item.nombreAsistente ||
        item.NombreAsistente ||
        item.nombre ||
        item.Nombre ||
        '—'
    },
    {
      key: 'gptClasicoModelo',
      title: 'Modelo',
      sortable: true,
      render: (value, item) => item.gptClasicoModelo || item.GPTClasicoModelo || '—'
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    {
      key: 'passwordAsistente',
      title: 'Password',
      sortable: false,
      render: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—'
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-edit"
            title="Editar GPT"
            onClick={() => handleEditAssistant(item)}
          >
            <i className="fas fa-pen"></i>
          </button>
          <button
            className="table-btn table-btn-share"
            title="Compartir URL"
            onClick={() => handleShareAssistant(item)}
          >
            <i className="fas fa-share-alt"></i>
          </button>
<button
            className="table-btn table-btn-test"
            title="Probar GPT"
            onClick={() => handleTestAssistant(item)}
          >
            <i className="fas fa-play"></i>
            <span>Probar</span>
          </button>
        </div>
      )
    }
  ];

  const handleNavigate = (path) => {
    setIsSidebarOpen(false);
    navigate(path);
  };
  const handleAdminNavigate = (itemId) => {
    setIsSidebarOpen(false);
    if (itemId === 'asistentes-gpt') {
      navigate('/gpt-assistants');
      return;
    }
    navigate('/admin-dashboard', { state: { tab: itemId } });
  };

  return (
    <div className={`new-dashboard my-assistants ${isAdminUser ? 'admin-mode' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdminUser ? (
            filteredAdminMenuItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${item.id === 'asistentes-gpt' ? 'active' : ''}`}
                onClick={() => handleAdminNavigate(item.id)}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
              </button>
            ))
          ) : (
            <>
              <button
                className="nav-item"
                onClick={() => handleNavigate('/dashboard')}
              >
                <i className="fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </button>
              <button
                className="nav-item"
                onClick={() => handleNavigate('/my-assistants')}
              >
                <i className="fas fa-list-ul"></i>
                <span>Mis Asistentes OpenAI</span>
              </button>
              <button className="nav-item active">
                <i className="fas fa-robot"></i>
                <span>Asistentes GPT</span>
              </button>
              <button
                className="nav-item"
                onClick={() => handleNavigate('/reports')}
              >
                <i className="fas fa-chart-bar"></i>
                <span>Reportes</span>
              </button>
            </>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || user?.roleName || user?.nombreRol || 'Usuario'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              Mis GPT Clásicos
            </h1>
            <p className="page-subtitle">Gestiona todos tus GPT Clásicos</p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {shareModal.open && (
            <div className="share-modal-overlay" onClick={closeShareModal}>
              <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="share-modal-header">
                  <div>
                    <p className="share-modal-eyebrow">Compartir GPT</p>
                    <h3>{shareModal.name || 'URL pública copiada'}</h3>
                  </div>
                  <button className="share-modal-close" onClick={closeShareModal} type="button" title="Cerrar">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="share-modal-body">
                  <p>La URL pública ya se copió al portapapeles. Podés pegarla y compartirla donde quieras.</p>
                  <div className="share-modal-input">
                    <input type="text" value={shareModal.url} readOnly />
                    <button type="button" onClick={() => handleShareAssistant({ gptClasicoURLPublica: shareModal.url })}>
                      Copiar
                    </button>
                  </div>
                </div>
                <div className="share-modal-footer">
                  <button className="btn btn-primary" onClick={closeShareModal}>Cerrar</button>
                </div>
              </div>
            </div>
          )}

          {/* Assistants Summary */}
          <div className="assistants-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-robot"></i>
              </div>
              <div className="summary-content">
                <h2 className="summary-number">{asistentes.length}</h2>
                <p className="summary-text">
                  {asistentes.length === 1 ? 'GPT creado' : 'GPTs creados'}
                </p>
                <div className="summary-description">
                  {asistentes.length === 0 
                    ? 'Aún no has creado ningún GPT Clásico'
                    : 'Listos para ayudar a los alumnos'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* CTA Nuevo GPT */}
          <div className="assistant-cta">
            <div className="cta-text">
              <h3>Crea un nuevo GPT Clásico</h3>
              <p>Comparte material y habilítalo para tus alumnos en segundos.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleNewAssistant}
            >
              <i className="fas fa-plus"></i>
              Nuevo GPT
            </button>
          </div>

          {/* Table */}
          <div className="content-card">
            <div className="card-header">
              <h2 className="card-title">
                <i className="fas fa-list"></i>
                Lista de GPT Clásicos
              </h2>
            </div>
            
            <div className="card-content">
              <DataTable
                data={asistentes}
                columns={columns}
                loading={isLoading}
                loadingMessage="Obteniendo mis GPTs..."
                emptyMessage="No tienes GPTs creados aún. ¡Crea tu primer GPT Clásico!"
                className="assistants-table"
              />
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default MyGptAssistants;
